import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { signInWithGoogle, logout } from "../firebase/authService";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase";
import { useNavigate } from "react-router-dom";
import NavLogin from "../components/NavLogin";
import { BsGoogle } from "react-icons/bs";
import Ptn from "../components/Ptn";
import { LuLogOut } from "react-icons/lu";
import { FiEdit } from "react-icons/fi";
import { GrGoogle } from "react-icons/gr";
import { RiEditLine } from "react-icons/ri";
import { MdOutlineHome } from "react-icons/md";

import defaultProfilePic from '../assets/img/defaultProfilePic.webp'
import BarLoader from "react-spinners/BarLoader";
import { toast } from "react-toastify";

const Login = ({ isScrolled }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, []);

    const handleLogin = async () => {
        setLoading(true); // Start spinner
        try {
            await signInWithGoogle();
        } catch (error) {
            // console.error("Login failed", error);
            setLoading(false); // Stop spinner
        } finally {
            setLoading(false); // Stop spinner
            toast.success(
                <>
                    <h5 className={`relative btnTxtSize text-center xsm:text-[0.7rem] sm:text-[0.725rem] xl:text-[0.75rem] 2xl:text-[0.8rem]`}>
                        Signed in
                    </h5>
                </>,
                {
                    position: 'bottom-center',
                    closeButton: false,
                    autoClose: 6000,
                    /* icon: <MdDownloadDone className='absolute left-0 text-white xsm:text-[1rem] sm:text-[1.15rem] xl:text-[1.25rem] 2xl:text-[1.5rem]' /> */
                }
            );
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login"); // Redirect back to login page
    };

    const handleClick = () => {
        navigate("/legalupdateeditor"); // Navigate to homepage

        setTimeout(() => {
            const homeSection = document.getElementById("legal_editor");
            if (homeSection) {
                const offset = 20; // Adjust scroll offset
                const targetY = homeSection.getBoundingClientRect().top + window.scrollY - offset;

                window.scrollTo({ top: targetY, behavior: "smooth" });
            }
        }, 100); // Delay ensures the page loads before scrolling
    };

    const handleHome = () => {
        navigate("/"); // Navigate to homepage

        setTimeout(() => {
            const homeSection = document.getElementById("home");
            if (homeSection) {
                const offset = 20; // Adjust scroll offset
                const targetY = homeSection.getBoundingClientRect().top + window.scrollY - offset;

                window.scrollTo({ top: targetY, behavior: "smooth" });
            }
        }, 100); // Delay ensures the page loads before scrolling
    };

    const fadeIn = {
        initial: {
            opacity: 0,
            y: 30
        },
        animate: {
            opacity: 1,
            y: 0,
            transition: {
                ease: "easeInOut",
                duration: 0.35,
                delay: 1
            }
        },
        exit: {
            opacity: 0,
            y: 30,
            transition: {
                ease: "easeInOut",
                duration: 0.75
            }
        },
    };

    return (
        <section
            id="login"
        >
            <AnimatePresence mode="wait">
                <motion.div
                    variants={fadeIn}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    <div
                        className="relative z-[800]"
                    >
                        <NavLogin
                            isScrolled={isScrolled}
                        />
                    </div>
                    <div className="flex flex-col items-center justify-center xs:h-[60vh] 2xl:h-[70vh] pt-[6rem]">
                        <div className="fixed top-0 left-0 xs:bg-gradient-to-t lg:bg-gradient-to-r from-bgSky via-bgSky to-bgSky/0 xs:w-full lg:w-[75%] h-full z-20" />
                        <Ptn
                            isScrolled={isScrolled}
                        />
                        {user ? (
                            <div
                                className="relative z-[200] flex flex-col items-center justify-center mt-[1rem]"
                            >
                                <img
                                    src={user.photoURL ? user.photoURL : defaultProfilePic}
                                    alt="User"
                                    className="trns500 group relative z-[1000] cursor-pointer bg-gradient-to-r from-primaryRichBlue to-primaryRichGreen flex items-center justify-center alata btnTxtSize tracking-[0px] text-white/90 hover:text-black xs:w-[4.5rem] xs:h-[4.5rem] xsp:w-[5rem] xsp:h-[5rem] lg:w-[4rem] lg:h-[4rem] xl:w-[4.5rem] xl:h-[4.5rem] 2xl:w-[5rem] 2xl:h-[5rem] rounded-full mx-auto xs:border-[3px] 2xl:border-[4px] border-slate-200/95 hover:border-slate-200 shadow-2xl xs:outline-[3px] 2xl:outline-[4px] outline-white/80 hover:outline-slate-900/80 btnTxtShadow my-[1rem] text-center"
                                />
                                <p
                                    className="outfit w-full text-center text-slate-600 font-[700] xs:text-[1rem] xsp:text-[1.15rem] md:text-[1.5rem] lg:text-[0.95rem] 2xl:text-[1.15rem] xs:mb-[1rem] lg:mb-[1rem] xs:pb-[1rem] lg:pb-[1.5rem] border-b border-slate-800/30"
                                >
                                    Welcome, {user.displayName}!
                                </p>
                                <button
                                    // onClick={() => navigate("/")}
                                    onClick={handleHome}
                                    className="bg-gradient-to-r from-teal-700 via-teal-600 to-teal-400 trns500 group relative z-[200] cursor-pointer flex items-center justify-center alata btnTxtSize tracking-[0px] text-white/90 hover:text-black xs:w-[18rem] md:w-[20rem] 2xl:w-[21rem] xs:h-[3.25rem] xsp:h-[3.5rem] lg:h-[3rem] xl:h-[3.5rem] 2xl:h-[4rem] rounded-full xs:border-[3px] 2xl:border-[4px] border-slate-200/95 hover:border-slate-200 shadow-2xl xs:outline-[3px] 2xl:outline-[4px] outline-white/80 hover:outline-slate-900/80 btnTxtShadow xs:my-[0.5rem] lg:my-[0.5rem] 2xl:my-[1rem]"
                                >
                                    Back to Home
                                    <MdOutlineHome className='trns500 absolute bg-white/20 group-hover:bg-slate-900 border-[2px] border-white/20 group-hover:border-slate-900/0 group-hover:text-white rounded-full xs:p-[0.45rem] lg:p-[0.6rem] xl:p-[0.6rem] xs:h-[2.25rem] xs:w-[2.25rem] md:h-[2.5rem] md:w-[2.5rem] lg:h-[2.25rem] lg:w-[2.25rem] xl:h-[2.5rem] xl:w-[2.5rem] 2xl:h-[2.75rem] 2xl:w-[2.75rem] xs:right-[0.3rem] md:right-[0.5rem] lg:right-[0.4rem] xl:right-[0.4rem] shadow-md' />
                                </button>
                                <button
                                    onClick={handleClick}
                                    className="trns500 group relative z-[1000] cursor-pointer bg-gradient-to-r from-primaryRichBlue to-primaryRichGreen flex items-center justify-center alata btnTxtSize tracking-[0px] text-white/90 hover:text-black xs:w-[18rem] md:w-[20rem] 2xl:w-[21rem] xs:h-[3.25rem] xsp:h-[3.5rem] lg:h-[3rem] xl:h-[3.5rem] 2xl:h-[4rem] rounded-full mx-auto xs:border-[3px] 2xl:border-[4px] border-slate-200/95 hover:border-slate-200 shadow-2xl xs:outline-[3px] 2xl:outline-[4px] outline-white/80 hover:outline-slate-900/80 btnTxtShadow xs:my-[0.5rem] lg:my-[0.5rem] 2xl:my-[1rem]"
                                >
                                    Edit Legal Bugle
                                    <RiEditLine className="trns500 absolute bg-white/20 group-hover:bg-slate-900 border-[2px] border-white/20 group-hover:border-slate-900/0 group-hover:text-white rounded-full xs:p-[0.45rem] lg:p-[0.6rem] xl:p-[0.65rem] xs:h-[2.25rem] xs:w-[2.25rem] md:h-[2.5rem] md:w-[2.5rem] lg:h-[2.25rem] lg:w-[2.25rem] xl:h-[2.5rem] xl:w-[2.5rem] 2xl:h-[2.75rem] 2xl:w-[2.75rem] xs:right-[0.3rem] md:right-[0.5rem] lg:right-[0.4rem] xl:right-[0.4rem] shadow-md" />
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="trns500 group relative z-[1000] cursor-pointer bg-gradient-to-r from-red-800 via-red-700 to-orange-600 flex items-center justify-center alata btnTxtSize tracking-[0px] text-white/90 hover:text-black xs:w-[18rem] md:w-[20rem] 2xl:w-[21rem] xs:h-[3.25rem] xsp:h-[3.5rem] lg:h-[3rem] xl:h-[3.5rem] 2xl:h-[4rem] rounded-full mx-auto xs:border-[3px] 2xl:border-[4px] border-red-200/95 hover:border-red-200 shadow-2xl xs:outline-[3px] 2xl:outline-[4px] outline-white/80 hover:outline-slate-900/80 btnTxtShadow xs:my-[0.5rem] lg:my-[0.5rem] 2xl:my-[1rem]"
                                >
                                    Logout
                                    <LuLogOut className="trns500 absolute bg-white/20 group-hover:bg-slate-900 border-[2px] border-white/20 group-hover:border-slate-900/0 group-hover:text-white rounded-full xs:p-[0.45rem] lg:p-[0.6rem] xl:p-[0.65rem] xs:h-[2.25rem] xs:w-[2.25rem] md:h-[2.5rem] md:w-[2.5rem] lg:h-[2.25rem] lg:w-[2.25rem] xl:h-[2.5rem] xl:w-[2.5rem] 2xl:h-[2.75rem] 2xl:w-[2.75rem] xs:right-[0.3rem] md:right-[0.5rem] lg:right-[0.4rem] xl:right-[0.4rem] shadow-md" />
                                </button>
                            </div>
                        ) : (
                            <>
                                <p
                                    className="outfit text-slate-600 font-[600] xs:text-[0.8rem] xsp:text-[0.9rem] md:text-[1rem] lg:text-[0.9rem] 2xl:text-[1.15rem] z-[200] xs:mb-[0.5rem] lg:mb-[1rem] xs:pb-[1rem] lg:pb-[1.5rem] border-b border-slate-800/30"
                                >
                                    Please sign-in to access legal bugle editing page
                                </p>
                                <button
                                    className="bg-gradient-to-r from-primaryRichBlue to-primaryRichGreen trns500 group relative z-[200] cursor-pointer flex items-center justify-center alata btnTxtSize tracking-[0px] text-white/90 hover:text-black xs:w-[18rem] md:w-[20rem] 2xl:w-[21rem] xs:h-[3.5rem] xsp:h-[4rem] lg:h-[3rem] xl:h-[3.5rem] 2xl:h-[4rem] rounded-full mx-auto border-[4px] border-slate-200/95 hover:border-slate-200 shadow-2xl outline-[4px] outline-white/80 hover:outline-slate-900/80 btnTxtShadow my-[1rem]"
                                    onClick={handleLogin}
                                >
                                    {loading ?
                                        <BarLoader height={2} width={60} color="#fff" />
                                        :
                                        <>
                                            Sign in with Google
                                            <GrGoogle
                                                className='trns500 absolute bg-white/20 group-hover:bg-slate-900 border-[2.5px] border-white/20 group-hover:border-slate-900/0 group-hover:text-white rounded-full xs:p-[0.55rem] lg:p-[0.6rem] xl:p-[0.7rem] xs:h-[2.25rem] xs:w-[2.25rem] md:h-[2.5rem] md:w-[2.5rem] lg:h-[2.25rem] lg:w-[2.25rem] xl:h-[2.5rem] xl:w-[2.5rem] 2xl:h-[2.75rem] 2xl:w-[2.75rem] xs:right-[0.5rem] md:right-[0.5rem] lg:right-[0.4rem] xl:right-[0.4rem] shadow-md'
                                            />
                                        </>
                                    }

                                </button>
                                <button
                                    // onClick={() => navigate("/")}
                                    onClick={handleHome}
                                    className="bg-gradient-to-r from-teal-700 via-teal-600 to-teal-400 trns500 group relative z-[200] cursor-pointer flex items-center justify-center alata btnTxtSize tracking-[0px] text-white/90 hover:text-black xs:w-[18rem] md:w-[20rem] 2xl:w-[21rem] xs:h-[3.5rem] xsp:h-[4rem] lg:h-[3rem] xl:h-[3.5rem] 2xl:h-[4rem] rounded-full xs:border-[3px] 2xl:border-[4px] border-slate-200/95 hover:border-slate-200 shadow-2xl xs:outline-[3px] 2xl:outline-[4px] outline-white/80 hover:outline-slate-900/80 btnTxtShadow my-[1rem]"
                                >
                                    Back to Home
                                    <MdOutlineHome className='trns500 absolute bg-white/20 group-hover:bg-slate-900 border-[2px] border-white/20 group-hover:border-slate-900/0 group-hover:text-white rounded-full xs:p-[0.45rem] lg:p-[0.6rem] xl:p-[0.6rem] xs:h-[2.25rem] xs:w-[2.25rem] md:h-[2.5rem] md:w-[2.5rem] lg:h-[2.25rem] lg:w-[2.25rem] xl:h-[2.5rem] xl:w-[2.5rem] 2xl:h-[2.75rem] 2xl:w-[2.75rem] xs:right-[0.3rem] md:right-[0.5rem] lg:right-[0.4rem] xl:right-[0.4rem] shadow-md' />
                                </button>
                            </>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </section>
    );
};

export default Login;
